import React, { useState, useMemo } from "react";
import DatePickerInput from "../DatePickerInput";
import { formatAccountName } from "../../../../utils/formatters";
import {
  WITHDRAWAL_METHOD_OPTIONS,
  FREQUENCY_OPTIONS,
  DESTINATION_TYPE_OPTIONS,
} from "../../../../constants/accountConstants";
import { getAccountBanks, formatBankLabel } from "../../../../utils/bankAccounts";
import { getHtmlDateMinMax } from "../../../../utils/dateFieldConstraints";

const TYPE_OPTIONS = ["Fixed Amount", "RMD", "Interest", "Income Only"];

const SKIP_KEYS = new Set(["id", "status"]);

const isMissing = (val) => val === null || val === undefined || val === "";

const WithdrawalsForm = ({ householdData, fieldData, missingFields, entityId, onSave, onSaveAndExit, saving, nextLabel }) => {
  const account = fieldData || {};
  const existingWithdrawals = account.withdrawals || [];

  const [formData, setFormData] = useState(() =>
    existingWithdrawals.map((w) => {
      const copy = {};
      Object.keys(w).forEach((k) => {
        if (!SKIP_KEYS.has(k)) copy[k] = w[k] ?? "";
      });
      return copy;
    })
  );

  const handleFieldChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === "destination_type") {
        updated[index].destination_identifier = "";
      }
      return updated;
    });
  };

  const REQUIRED_FIELDS = ["type", "amount", "frequency", "start_date"];
  const scheduleBounds = getHtmlDateMinMax("schedule_future");
  const totalMissing = existingWithdrawals.reduce(
    (sum, w) => sum + REQUIRED_FIELDS.filter((k) => isMissing(w[k])).length, 0
  );

  const brokerageOptions = useMemo(() => {
    const accounts = householdData?.accounts || [];
    const members = householdData?.members || householdData?.household_members || [];
    return accounts
      .filter((a) => a.id !== entityId)
      .map((acc) => ({
        value: acc.id,
        label: formatAccountName(
          acc,
          (() => {
            const idx = accounts.findIndex((a) => String(a.id) === String(acc.id));
            return idx >= 0 ? idx + 1 : 1;
          })(),
          members,
        ),
      }));
  }, [householdData, entityId]);

  const bankAccountOptions = useMemo(() => {
    const allAccounts = householdData?.accounts || [];
    const seen = new Set();
    const options = [];
    allAccounts.forEach((acc) => {
      getAccountBanks(acc).forEach((ba) => {
        if (!ba?.id || seen.has(ba.id)) return;
        seen.add(ba.id);
        options.push({ value: ba.id, label: formatBankLabel(ba) });
      });
    });
    return options;
  }, [householdData]);

  const getDestLabel = (destType, destId) => {
    if (!destId) return "";
    if (destType === "Brokerage Account") {
      const opt = brokerageOptions.find((o) => o.value === destId);
      return opt ? opt.label : destId;
    }
    if (destType === "Bank Account") {
      const opt = bankAccountOptions.find((o) => o.value === destId);
      return opt ? opt.label : destId;
    }
    return destId;
  };

  const buildPayload = () => {
    const update = {};
    formData.forEach((w, i) => {
      const orig = existingWithdrawals[i] || {};
      if (!orig.id) return;
      update[orig.id] = {
        federal_tax_withholding: w.federal_tax_withholding !== "" ? parseFloat(w.federal_tax_withholding) : null,
        state_tax_withholding: w.state_tax_withholding !== "" ? parseFloat(w.state_tax_withholding) : null,
        amount: w.amount !== "" ? parseFloat(w.amount) : null,
        frequency: w.frequency || null,
        start_date: w.start_date || null,
        type: w.type || null,
        method: w.method || null,
        destination_type: w.destination_type || null,
        destination_identifier: w.destination_identifier || null,
      };
    });
    return { accounts: { [entityId]: { withdrawals: { update } } } };
  };

  const handleSubmit = (cb) => cb(buildPayload());

  if (existingWithdrawals.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">No withdrawal records to complete.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        {totalMissing > 0 && (
          <span className="text-sm text-red-500 font-medium">
            ● {totalMissing} Required field{totalMissing !== 1 ? "s" : ""} missing
          </span>
        )}
      </div>

      {existingWithdrawals.map((w, i) => {
        const data = formData[i] || {};
        const destType = data.destination_type || "";
        const destOptions = destType === "Brokerage Account" ? brokerageOptions : destType === "Bank Account" ? bankAccountOptions : [];

        return (
          <div key={w.id || i} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <span className="bg-emerald-100 text-emerald-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">{i + 1}</span>
                Withdrawal Details
              </h3>
              {REQUIRED_FIELDS.some((k) => isMissing(w[k])) && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase tracking-wider border border-red-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                  Details Required
                </span>
              )}
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Type {isMissing(w.type) && <span className="text-red-500">*</span>}
                  </label>
                  <select value={data.type || ""} onChange={(e) => handleFieldChange(i, "type", e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all">
                    <option value="">Select type</option>
                    {TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Amount ($) {isMissing(w.amount) && <span className="text-red-500">*</span>}
                  </label>
                  <input type="number" min={0} value={data.amount ?? ""} onChange={(e) => handleFieldChange(i, "amount", e.target.value)} placeholder="0.00" className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Frequency {isMissing(w.frequency) && <span className="text-red-500">*</span>}
                  </label>
                  <select value={data.frequency || ""} onChange={(e) => handleFieldChange(i, "frequency", e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all">
                    <option value="">Select Frequency</option>
                  {FREQUENCY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Start Date {isMissing(w.start_date) && <span className="text-red-500">*</span>}
                  </label>
                  <DatePickerInput value={data.start_date || ""} onChange={(e) => handleFieldChange(i, "start_date", e.target.value)} min={scheduleBounds.min} max={scheduleBounds.max} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Method</label>
                  <select value={data.method || ""} onChange={(e) => handleFieldChange(i, "method", e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all">
                    <option value="">Select Method</option>
                    {WITHDRAWAL_METHOD_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Federal Tax Withholding (%)</label>
                  <input type="number" min={0} max={100} value={data.federal_tax_withholding ?? ""} onChange={(e) => handleFieldChange(i, "federal_tax_withholding", e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">State Tax Withholding (%)</label>
                  <input type="number" min={0} max={100} value={data.state_tax_withholding ?? ""} onChange={(e) => handleFieldChange(i, "state_tax_withholding", e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Destination Type</label>
                  <select value={destType} onChange={(e) => handleFieldChange(i, "destination_type", e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all">
                    <option value="">Select Destination</option>
                  {DESTINATION_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                {destType && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{destType}</label>
                    <select value={data.destination_identifier || ""} onChange={(e) => handleFieldChange(i, "destination_identifier", e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all">
                      <option value="">Select {destType}</option>
                      {destOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {!destType && data.destination_identifier && (
                <div className="text-xs text-gray-500">
                  Linked to: {getDestLabel(w.destination_type, data.destination_identifier)}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">Save & Exit</button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default WithdrawalsForm;
